var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Loader, Table, TableSize } from '@ui';
import { Text } from '@ui/atoms/Text';
import { Tag } from '@ui/atoms/Tag';
import { TextInput } from '@ui/molecules/inputs/form/TextInput';
import { ConfirmDialog } from '@ui/molecules/ConfirmDialog';
import { useDebouncedListingSearch } from '@hooks/listings';
import { DefaultDebouceDelay } from '@constants/configs';
import { translate } from '@utils/i18n';
import { getFolders, deleteFolder, lockFolder, unlockFolder } from '../../services/CustomerFileDropService';
import { QUERY_KEYS } from '../../hooks/queryKeys';
import { CreateFolderModal } from '../CreateFolderModal';
import { RenameFolderModal } from '../RenameFolderModal';
import './FolderListStyles.scss';
const namespace = 'features.settings.customer_file_drop';
function formatDate(iso) {
    return new Date(iso).toLocaleDateString();
}
function isExpired(iso) {
    return new Date(iso) < new Date();
}
function FolderList({ canUseDrop, canLockFolders, onSelectFolder }) {
    var _a, _b, _c, _d, _e;
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [folderToDelete, setFolderToDelete] = useState(null);
    const [folderToRename, setFolderToRename] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [lockingId, setLockingId] = useState(null);
    const queryClient = useQueryClient();
    const commitSearch = useCallback((value) => {
        setSearch(value);
        setPage(1);
    }, []);
    const { searchInput, handleSearchChange } = useDebouncedListingSearch({
        setSearch: commitSearch,
        delayMs: DefaultDebouceDelay
    });
    const { data, isLoading, isError } = useQuery({
        queryKey: QUERY_KEYS.folders(page, search),
        queryFn: () => getFolders(page, 25, search || undefined)
    });
    const handleFolderCreated = () => {
        void queryClient.invalidateQueries({ queryKey: ['customer_file_drop', 'folders'] });
    };
    const handleConfirmDelete = () => __awaiter(this, void 0, void 0, function* () {
        if (!folderToDelete)
            return;
        setIsDeleting(true);
        try {
            yield deleteFolder(folderToDelete.id);
            setFolderToDelete(null);
            void queryClient.invalidateQueries({ queryKey: ['customer_file_drop', 'folders'] });
        }
        catch (_a) {
            window.alert(translate('folder_delete_error', { namespace }));
        }
        finally {
            setIsDeleting(false);
        }
    });
    const handleToggleLock = (folder) => __awaiter(this, void 0, void 0, function* () {
        setLockingId(folder.id);
        try {
            if (folder.locked) {
                yield unlockFolder(folder.id);
            }
            else {
                yield lockFolder(folder.id);
            }
            void queryClient.invalidateQueries({ queryKey: ['customer_file_drop', 'folders'] });
        }
        catch (_a) {
            window.alert(translate(folder.locked ? 'folder_unlock_error' : 'folder_lock_error', { namespace }));
        }
        finally {
            setLockingId(null);
        }
    });
    const folders = (_a = data === null || data === void 0 ? void 0 : data.folders) !== null && _a !== void 0 ? _a : [];
    const meta = data === null || data === void 0 ? void 0 : data.meta;
    const showActions = canUseDrop || canLockFolders;
    const columns = useMemo(() => [
        {
            dataKey: 'id',
            label: translate('column_id', { namespace }),
            renderer: ({ item }) => (React.createElement(Text, { variant: "body", className: "folder-list__id" }, item.id))
        },
        {
            dataKey: 'name',
            label: translate('column_name', { namespace }),
            columnType: 'primary',
            renderer: ({ item }) => (React.createElement("button", { type: "button", className: "folder-list__name-btn", "data-testid": "folder-row", onClick: () => onSelectFolder(item) },
                React.createElement("i", { className: `fa-light ${item.locked ? 'fa-lock' : 'fa-folder'}`, "aria-hidden": "true" }),
                React.createElement(Text, { variant: "body" }, item.name),
                item.locked ? React.createElement(Tag, { label: translate('locked_badge', { namespace }), variant: "gray" }) : null))
        },
        {
            dataKey: 'description',
            label: translate('column_description', { namespace }),
            renderer: ({ item }) => (React.createElement(Text, { variant: "body", className: "folder-list__description" }, item.description || '—'))
        },
        {
            dataKey: 'expiresAt',
            label: translate('column_expires', { namespace }),
            renderer: ({ item }) => (React.createElement(Tag, { label: isExpired(item.expiresAt)
                    ? translate('expired_badge', { namespace })
                    : translate('expires_badge', { namespace, date: formatDate(item.expiresAt) }), variant: isExpired(item.expiresAt) ? 'red' : 'gray' }))
        },
        {
            dataKey: 'filesCount',
            label: translate('column_files', { namespace }),
            align: 'right',
            renderer: ({ item }) => (React.createElement(Text, { variant: "body" }, translate('files_count', { namespace, count: item.filesCount })))
        },
        ...(showActions
            ? [
                {
                    dataKey: 'actions',
                    label: translate('column_actions', { namespace }),
                    columnType: 'action',
                    renderer: ({ item }) => (React.createElement("div", { className: "folder-list__actions" },
                        canLockFolders ? (React.createElement(Button, { variant: "secondary", onClick: () => void handleToggleLock(item), disabled: lockingId === item.id, "aria-label": translate(item.locked ? 'unlock_folder' : 'lock_folder', { namespace }), "data-testid": item.locked ? `unlock-folder-${item.id}` : `lock-folder-${item.id}` },
                            React.createElement("i", { className: `fa-light ${item.locked ? 'fa-lock-open' : 'fa-lock'}`, "aria-hidden": "true" }))) : null,
                        canUseDrop ? (React.createElement(Button, { variant: "secondary", onClick: () => setFolderToRename(item), disabled: item.locked, "aria-label": translate('rename_folder', { namespace }), title: item.locked
                                ? translate('folder_locked_hint', { namespace })
                                : translate('rename_folder', { namespace }), "data-testid": `rename-folder-${item.id}` },
                            React.createElement("i", { className: "fa-light fa-pen", "aria-hidden": "true" }))) : null,
                        canUseDrop ? (React.createElement(Button, { variant: "secondary", onClick: () => setFolderToDelete(item), disabled: item.locked, "aria-label": translate('folder_delete_confirm', {
                                namespace,
                                name: item.name
                            }), title: item.locked ? translate('folder_locked_hint', { namespace }) : undefined },
                            React.createElement("i", { className: "fa-light fa-trash", "aria-hidden": "true" }))) : null))
                }
            ]
            : [])
    ], [canUseDrop, canLockFolders, showActions, onSelectFolder, lockingId]);
    if (isLoading) {
        return (React.createElement("div", { className: "folder-list__loading" },
            React.createElement(Loader, { size: "48" })));
    }
    if (isError) {
        return (React.createElement("div", { className: "folder-list__error" },
            React.createElement(Text, { variant: "body" }, translate('loading_error', { namespace }))));
    }
    return (React.createElement("div", { className: "folder-list", "data-testid": "folder-list" },
        React.createElement("div", { className: "folder-list__header" },
            React.createElement(Text, { variant: "large", as: "h2" }, translate('folders_heading', { namespace })),
            canUseDrop && (React.createElement(Button, { onClick: () => setIsCreateOpen(true), "data-testid": "create-folder-button" },
                React.createElement("i", { className: "fa-light fa-plus", "aria-hidden": "true" }),
                translate('create_folder', { namespace })))),
        React.createElement(TextInput, { id: "customer-file-drop-folder-search", label: translate('search_folders', { namespace }), value: searchInput, onChange: (e) => handleSearchChange(e.target.value) }),
        folders.length === 0 ? (React.createElement("div", { className: "folder-list__empty" },
            React.createElement(Text, { variant: "body" }, translate('no_folders', { namespace })))) : (React.createElement(Table, { data: folders, columns: columns, size: TableSize.Full, variant: "listing", getItemId: (folder) => folder.id, onPageChange: setPage, serverCurrentPage: (_b = meta === null || meta === void 0 ? void 0 : meta.currentPage) !== null && _b !== void 0 ? _b : page, serverTotalPages: (_c = meta === null || meta === void 0 ? void 0 : meta.totalPages) !== null && _c !== void 0 ? _c : 1, serverTotalItems: (_d = meta === null || meta === void 0 ? void 0 : meta.totalCount) !== null && _d !== void 0 ? _d : folders.length, pageSize: (_e = meta === null || meta === void 0 ? void 0 : meta.perPage) !== null && _e !== void 0 ? _e : 25 })),
        React.createElement(CreateFolderModal, { isOpen: isCreateOpen, onClose: () => setIsCreateOpen(false), onCreated: handleFolderCreated }),
        React.createElement(RenameFolderModal, { folder: folderToRename, isOpen: folderToRename != null, onClose: () => setFolderToRename(null), onRenamed: () => {
                void queryClient.invalidateQueries({ queryKey: ['customer_file_drop', 'folders'] });
            } }),
        React.createElement(ConfirmDialog, { isOpen: folderToDelete != null, title: translate('delete_folder_title', { namespace }), description: folderToDelete ? translate('folder_delete_confirm', { namespace, name: folderToDelete.name }) : '', confirmLabel: translate('delete_folder_confirm', { namespace }), confirmVariant: "danger", cancelLabel: translate('cancel', { namespace: 'features.commons' }), loading: isDeleting, onConfirm: () => void handleConfirmDelete(), onCancel: () => setFolderToDelete(null) })));
}
export default FolderList;
